/**
 * i18n configuration for react-i18next
 *
 * English translations are bundled eagerly (always needed as fallback) but live
 * in ./resourcesEn.ts — see registerEnglishBundles() below for why.
 * Other languages are loaded on demand when the user switches language.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import {
  UI_LANGUAGE_VALUES,
  uiLanguageDir,
  uiLanguageNativeName,
  type UiLanguage,
} from '@/generated/config-enums';

import { registerAgreeFormatter } from './agreeFormatter';
import { TERMINOLOGY_DEFAULT_VARIABLES } from './terminologyDefaults';


/** A shipped UI language's code — the registered `locale § languages § values` set. */
export type SupportedLanguageCode = UiLanguage;

/**
 * One shipped UI language, as every selector and direction lookup reads it.
 *
 * Named `…Entry` because `shared/auth/types.ts` already exports a
 * `SupportedLanguage`, and that one is the CODE (`= UiLanguage`) — two things
 * called the same name, one a string and one an object, is how a call site picks
 * the wrong import off an editor's suggestion list.
 */
export interface SupportedLanguageEntry {
  code: SupportedLanguageCode;
  /**
   * The language's ENDONYM ("עברית", not a translated "Hebrew"). Deliberately not
   * translated: a Hebrew speaker hunting for their language in a Spanish UI looks
   * for their own script. The translated name is the selectors' SECOND line and
   * lives in `common:languages.<code>`, which the three language selectors render
   * as each option's second line — held to this value set, in every locale, by
   * tools/check_config_enum_i18n_label_map.py.
   */
  nativeName: string;
  dir: 'ltr' | 'rtl';
}

/**
 * The languages the app ships, DERIVED from the registered config group rather
 * than spelled here (WI 163d34e4).
 *
 * This array used to hand-copy `shared/config/locale.config.json § languages §
 * values` — the last copy of that value set to survive WI 82d94165, and one no
 * gate could see: `check_handwritten_enum_union.py` matches string-literal
 * unions, and an object array carrying `nativeName`/`dir` is neither.
 * `tools/check_handwritten_enum_object_array.py` sees the shape now.
 *
 * It reaches the generated bindings through `@/generated/config-enums`, NOT
 * through `shared/config/locale.config.json`. That is load-bearing for
 * merge-and-ship's frontend gate: this module sits in ~67% of test import
 * graphs, and the raw config file also holds currencies, countries and both
 * format groups — so a static import of the JSON would put an edit to ANY of
 * them into that impact set. The barrel is one module per enum group
 * (`generated/config-enums/ui-language.ts`), narrowed by
 * `apps/frontend/vitest.barrelGraph.ts`, so only a change to the LANGUAGE group
 * reaches here. See tools/check_vitest_impact_hubs.py.
 */
export const SUPPORTED_LANGUAGES: readonly SupportedLanguageEntry[] = UI_LANGUAGE_VALUES.map(
  (code) => ({ code, nativeName: uiLanguageNativeName[code], dir: uiLanguageDir[code] }),
);

// Default language
export const DEFAULT_LANGUAGE: SupportedLanguageCode = 'en';

// Default terminology variables — resolved at runtime via tenant overrides.
// These EN defaults ensure strings render correctly even before the API loads.
// They live in their own leaf module so navigation search can read the canonical
// English vocabulary without importing i18next initialisation; re-exported here
// because this is where i18next is initialised with them, and because the
// errors.json placeholder-contract test asserts against the same set.
export { TERMINOLOGY_DEFAULT_VARIABLES };

// Namespace list shared between init and lazy loaders.
// Exported so tests can assert every non-English loader covers all namespaces
// (guards against the loader-drift bug where a fully-translated namespace was
// never wired into the es/he lazy loaders and silently fell back to English).
export const ALL_NAMESPACES = ['common', 'auth', 'tasks', 'user_management', 'accounting', 'file_management', 'aiAgents', 'pipeline', 'email', 'admin', 'ai_intake', 'sysadmin', 'coding', 'developer', 'recruiting', 'careers', 'people', 'aiChat', 'messaging', 'errors', 'messages', 'avatars', 'products', 'contacts', 'support', 'external', 'design_system', 'websiteHosting', 'shipments', 'customs', 'formsBpm', 'reminders', 'inventory', 'procurement', 'softwareFactory', 'notifications', 'monitoring', 'marketing', 'contracts', 'sales', 'brokerage', 'billing', 'chatbot', 'onboarding', 'custom_fields', 'whatsapp'] as const;

export type I18nNamespace = (typeof ALL_NAMESPACES)[number];

/**
 * Shape every per-language resource module (resources.es.ts / resources.he.ts /
 * resources.zh.ts) must default-export: one bundle per namespace. Typing it as
 * an exact Record over ALL_NAMESPACES makes tsc fail the resource module itself
 * when a new namespace is added to ALL_NAMESPACES but not wired into a language
 * — compile-time protection on top of the languageLoaderCoverage runtime test.
 */
export type LanguageResourceBundles = Record<I18nNamespace, Record<string, unknown>>;

// Track which languages have been loaded
const loadedLanguages = new Set<string>(['en']);

// Languages served by a lazy per-language resource module (everything but the
// eagerly-bundled English).
const LAZY_LANGUAGES = new Set<string>(
  SUPPORTED_LANGUAGES.map((l) => l.code).filter((code) => code !== DEFAULT_LANGUAGE),
);

/**
 * Import a language's resource module (resources.es.ts / resources.he.ts /
 * resources.zh.ts — one bundle per namespace, see LanguageResourceBundles).
 *
 * The COMPUTED specifier is load-bearing (WI d36e25d5) — do not "simplify" it
 * to literal `import('./resources.es')` calls or an `import.meta.glob`:
 *
 * - vitest's `--changed` impact analysis follows LITERAL static and dynamic
 *   import specifiers. With literal per-file imports here, every es/he/zh
 *   locale JSON was a transitive dependency of config.ts — and config.ts is in
 *   virtually every component test's graph (via shared/utils/errors.ts etc.),
 *   so ANY non-EN locale edit dirtied ~940 of ~1190 test files in the
 *   merge-and-ship frontend gate. A computed specifier has no statically
 *   recordable target, so non-EN JSONs stay out of the test graph. English is
 *   no longer statically imported here either (WI 151e3bed) — it lives in
 *   ./resourcesEn.ts and reaches this module through registerEnglishBundles();
 *   see that file's TEST-GRAPH CONTRACT for why, and note it is deliberately
 *   NOT named `resources.en.ts`, so the glob below cannot match it.
 * - At build time Vite's built-in dynamic-import-vars support globs
 *   `./resources.*.ts` and emits one lazy chunk per language, so runtime
 *   behavior (all languages bundled and switchable) is unchanged. In dev and
 *   vitest the specifier resolves at runtime. The `.ts` extension is required
 *   for the glob (dynamic-import-vars needs an extension pattern).
 */
async function loadLanguageResources(lang: string): Promise<LanguageResourceBundles> {
  const mod = (await import(`./resources.${lang}.ts`)) as { default: LanguageResourceBundles };
  return mod.default;
}

/**
 * Load translations for a language on demand.
 * Uses i18n.addResourceBundle() to inject them at runtime.
 */
async function loadLanguageBundles(lang: string): Promise<void> {
  if (loadedLanguages.has(lang)) return;

  if (!LAZY_LANGUAGES.has(lang)) {
    // Mark as loaded even without a resource module to prevent infinite
    // re-trigger (e.g. LanguageDetector returns "en-US" which has no resource
    // module but isn't "en")
    loadedLanguages.add(lang);
    return;
  }

  const bundles = await loadLanguageResources(lang);
  for (const [ns, translations] of Object.entries(bundles)) {
    i18n.addResourceBundle(lang, ns, translations, true, true);
  }
  loadedLanguages.add(lang);
}

/**
 * Load translations for a language, then switch to it.
 * Call this instead of i18n.changeLanguage() to ensure translations
 * are available before the UI re-renders.
 */
export async function changeLanguage(lang: string): Promise<void> {
  await loadLanguageBundles(lang);
  await i18n.changeLanguage(lang);
}

/**
 * Name of BOTH the localStorage key and the cross-subdomain cookie that hold the
 * user's chosen UI language. localStorage is origin-scoped, so a language picked
 * or account-synced on `sysadmin.panluma.ai` is invisible to `app.panluma.ai` —
 * the single canonical Google/Microsoft SSO-callback host. That left the
 * post-SSO callback and the 2FA challenge it renders falling back to the
 * browser/GeoIP default (e.g. Spanish). A domain-scoped cookie is the one store
 * every `*.panluma.ai` subdomain shares, so it carries the known preference
 * across the SSO hop.
 */
export const LANGUAGE_STORAGE_KEY = 'panluma_language';
const LANGUAGE_COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

/**
 * Widen the language cookie to the registrable parent of any `*.panluma.ai` host
 * so `app` / `sysadmin` / tenant subdomains all share it. localhost and
 * fully-custom tenant domains stay host-scoped (no Domain attribute) — a custom
 * domain is a single host and over-widening to its public suffix would be
 * rejected by the browser.
 */
function languageCookieDomain(): string | undefined {
  const host = window.location.hostname;
  if (host === 'panluma.ai' || host.endsWith('.panluma.ai')) return '.panluma.ai';
  return undefined;
}

/** Read the cross-subdomain known-language cookie (undefined when unset). */
export function readKnownLanguageCookie(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${LANGUAGE_STORAGE_KEY}=([^;]*)`),
  );
  return match ? decodeURIComponent(match[1]) : undefined;
}

/**
 * Persist a *known* language preference — the account `preferred_language` or an
 * explicit LanguageSelector choice — to the cross-subdomain cookie AND
 * localStorage. This is the single writer of the authoritative cookie: the
 * i18next detector caches only to localStorage (see `detection.caches`), so a
 * transient GeoIP guess on one subdomain never masquerades as a known preference
 * to the others. Once set, it is read ahead of localStorage/navigator on every
 * subdomain (and by useGeoLanguageDefault to suppress the GeoIP upgrade).
 */
export function persistKnownLanguage(lang: string): void {
  if (typeof document === 'undefined') return;
  // Defense-in-depth: only ever persist a supported code. Every current caller
  // already passes a validated code, but this guarantees an unsupported value can
  // never reach the cross-subdomain cookie via a future caller.
  if (!SUPPORTED_LANGUAGES.some((l) => l.code === lang)) return;
  const domain = languageCookieDomain();
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    `${LANGUAGE_STORAGE_KEY}=${encodeURIComponent(lang)}` +
    `; Max-Age=${LANGUAGE_COOKIE_MAX_AGE_SECONDS}; Path=/` +
    `${domain ? `; Domain=${domain}` : ''}; SameSite=Lax${secure}`;
  localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
}

/**
 * Expire the cross-subdomain known-language cookie. Used when ending sysadmin
 * emulation: the emulated user's language was written cross-subdomain during the
 * session and must not leak onto the sysadmin's own session after restore. Match
 * the same Domain/Path so the browser actually removes it.
 */
export function clearKnownLanguageCookie(): void {
  if (typeof document === 'undefined') return;
  const domain = languageCookieDomain();
  // Deletion matches on Name+Domain+Path only, but keep SameSite for symmetry with
  // persistKnownLanguage so the cookie format stays the canonical one.
  document.cookie =
    `${LANGUAGE_STORAGE_KEY}=; Max-Age=0; Path=/${domain ? `; Domain=${domain}` : ''}; SameSite=Lax`;
}

/**
 * Register the eagerly-bundled English resources.
 *
 * English is NOT imported here any more (WI 151e3bed). config.ts sits in
 * virtually every component test's import graph via shared/utils/errors.ts and
 * shared/auth/index.ts, so a static `./locales/en/*.json` import from this file
 * put 1546 of 1899 test files into merge-and-ship's `vitest --changed` impact
 * set on any English string edit. The JSONs now live in ./resourcesEn.ts, which
 * calls this on import and is reached by exactly one static edge in the app
 * (src/index.tsx) and by a computed dynamic import in tests (src/setupTests.ts).
 *
 * Called synchronously immediately after this module initialises i18next, so
 * every consumer still sees a fully-populated English store — the bundles land
 * before anything renders. Registers through the same i18n.addResourceBundle call
 * the lazy languages use, so a bundle enters the store exactly one way — note this
 * repeats loadLanguageBundles' loop rather than calling it, because that function
 * also owns the dynamic import and the loadedLanguages bookkeeping, neither of
 * which applies to English (it is eager, and 'en' is pre-seeded as loaded).
 */
export function registerEnglishBundles(bundles: LanguageResourceBundles): void {
  for (const [ns, translations] of Object.entries(bundles)) {
    i18n.addResourceBundle(DEFAULT_LANGUAGE, ns, translations, true, true);
  }
}

// Initialize i18next. English arrives via registerEnglishBundles() above, called
// from ./resourcesEn.ts — `partialBundledLanguages` tells i18next that bundles
// are supplied outside init() so it does not treat the empty set as "no
// translations available" and suspend.
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {},
    partialBundledLanguages: true,
    fallbackLng: DEFAULT_LANGUAGE,
    // Pin the accepted language set to the single source of truth. Without this
    // the LanguageDetector would set i18n.language to any detected code (e.g. a
    // browser/GeoIP "fr" we don't translate, or "en-GB"); `nonExplicitSupportedLngs`
    // folds region variants ("en-US" -> "en") onto a supported base so the lazy
    // loader and dir/RTL lookups always see a known code.
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    nonExplicitSupportedLngs: true,
    defaultNS: 'common',
    ns: [...ALL_NAMESPACES],
    interpolation: {
      escapeValue: false, // React already escapes values
      defaultVariables: TERMINOLOGY_DEFAULT_VARIABLES,
    },
    detection: {
      // `querystring` first so a pre-auth page reached from an account email link
      // (reset-password, verify-email, /join, external portals) renders in the
      // language the email was sent in. The backend appends `?lang=<preferred>` to
      // those links (shared/utils/frontend_url.py).
      //
      // `cookie` next: the cross-subdomain known-language cookie (see
      // persistKnownLanguage). localStorage is origin-scoped, so when Google/MS SSO
      // redirects from `sysadmin.panluma.ai` to the canonical callback host
      // `app.panluma.ai`, the language chosen on sysadmin lives in a different
      // origin's localStorage. The cookie carries it across the hop so the SSO
      // callback + 2FA challenge render in the most-recently-signed-in user's
      // language. Without a session the app would otherwise fall back to the
      // browser/GeoIP default. Once authenticated, UserLanguageSync re-asserts the
      // account's preferred_language. `caches` stays localStorage-only — the cookie
      // is written exclusively by persistKnownLanguage so GeoIP guesses never
      // pollute it.
      order: ['querystring', 'cookie', 'localStorage', 'navigator'],
      lookupQuerystring: 'lang',
      lookupCookie: LANGUAGE_STORAGE_KEY,
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    react: {
      useSuspense: false, // Disable suspense for simplicity
    },
  });

/**
 * Stamp an email link's `?lang=` into the cross-subdomain known-language cookie.
 *
 * `?lang=` is NOT a guess: the backend only appends it to links it mailed to a
 * recipient whose language it had already resolved, and the invariant is that the
 * linked page renders in the same language as the email body. But the querystring
 * detector's result is cached to localStorage only — origin-scoped — so it died at
 * the first navigation off the landing page.
 *
 * That is what left an invited Hebrew-tenant user in English: `/join?lang=he`
 * rendered Hebrew, then "Continue with Google" hopped to the canonical SSO host
 * (`app.panluma.ai`, a different origin) and the language was simply gone —
 * through the SSO callback, the consent step, and all of onboarding. Promoting it
 * to the cookie is the same carry the account preference and an explicit selector
 * choice already get (see `persistKnownLanguage`); GeoIP guesses still never reach
 * the cookie.
 */
export function persistEmailLinkLanguage(): void {
  if (typeof window === 'undefined') return;
  const fromLink = new URLSearchParams(window.location.search).get('lang');
  // persistKnownLanguage validates the code, so an unsupported ?lang= is a no-op.
  if (fromLink) persistKnownLanguage(fromLink.split('-')[0].toLowerCase());
}

// `{{companyGender, agree(m: un; f: una)}}` — grammatical agreement with whatever word
// the tenant chose for a customizable term. Registered right after init(), before any
// bundle can be rendered. See ./agreeFormatter.
registerAgreeFormatter(i18n);

persistEmailLinkLanguage();

// Safety net: if someone calls i18n.changeLanguage() directly, still load bundles.
// The UI will briefly show English fallback keys then re-render once bundles arrive.
// Prefer using the exported changeLanguage() wrapper to avoid this flash.
i18n.on('languageChanged', (lang) => {
  if (lang !== 'en' && !loadedLanguages.has(lang)) {
    loadLanguageBundles(lang).then(() => {
      // Force re-render after bundles are injected
      i18n.emit('languageChanged', lang);
    });
  }
});

// If the detected language at startup is not English, load it immediately
if (i18n.language && i18n.language !== 'en') {
  loadLanguageBundles(i18n.language).then(() => {
    // Re-trigger so react-i18next re-renders with the loaded translations
    i18n.changeLanguage(i18n.language);
  });
}

/**
 * Apply tenant i18n overrides fetched from the API.
 * Called by the TerminologyProvider after loading tenant data.
 */
export function applyTenantI18nOverrides(
  locale: string,
  terms: Record<string, string>,
  overrides: Array<{ string_key: string; override_value: string }>,
): void {
  // Set curated terms as default interpolation variables
  // e.g., {{product}} will resolve to "Module" if tenant overrode "Product" -> "Module"
  i18n.options.interpolation = {
    ...i18n.options.interpolation,
    defaultVariables: {
      ...i18n.options.interpolation?.defaultVariables,
      ...terms,
    },
  };

  // Apply arbitrary per-key string overrides (escape hatch)
  if (Array.isArray(overrides)) {
    for (const { string_key, override_value } of overrides) {
      const colonIdx = string_key.indexOf(':');
      if (colonIdx > 0) {
        const namespace = string_key.substring(0, colonIdx);
        const key = string_key.substring(colonIdx + 1);
        i18n.addResource(locale, namespace, key, override_value);
      }
    }
  }

  // Force re-render so components pick up the new defaultVariables
  i18n.emit('languageChanged', i18n.language);
}

export default i18n;
